import { z } from 'zod/v4';
import { CustomerCategorySchema } from './customer-category.js';
// Payment method options
export const PaymentMethodSchema = z.enum([
    'credit-card',
    'ach',
    'check',
    'invoice',
    'prepaid',
]);
// Payment frequency options
export const PaymentFrequencySchema = z.enum([
    'per-order',
    'daily',
    'weekly',
    'monthly',
]);
// Payment status
export const PaymentStatusSchema = z.enum([
    'current',
    'past-due',
    'suspended',
    'prepaid',
]);
// QuickBooks sync status
export const QuickBooksSyncStatusSchema = z.enum([
    'synced',
    'pending',
    'error',
    'not-linked',
]);
// Account type
export const SetupAccountTypeSchema = z.enum([
    'individual',
    'organization',
]);
// Account status
export const SetupAccountStatusSchema = z.enum([
    'active',
    'inactive',
    'suspended',
    'closed',
]);
// Account user (users linked to an account)
export const AccountUserSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    auth0Id: z.string(),
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    role: z.enum(['owner', 'admin', 'member', 'viewer']),
    isAttorney: z.boolean().default(false),
    barNumber: z.string().optional(),
    // The five member states (SOR-590), in vocabulary order — one of the three
    // places the vocabulary is declared (the `account_user_status` DB enum and
    // the gateway's `MembershipStatus` are the other two). The order is
    // load-bearing: `MEMBER_STATES` in `account-state-machine.ts` reads this
    // enum's own `options`, so the runtime list and this union cannot drift.
    status: z.enum(['pending', 'active', 'inactive', 'suspended', 'erased']),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAccountUserSchema = AccountUserSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateAccountUserSchema = AccountUserSchema.partial().omit({
    id: true,
    accountId: true,
    createdAt: true,
});
// Main account schema (Auth0 customer + local metadata)
export const AccountSchema = z.object({
    id: z.string().uuid(),
    auth0Id: z.string().optional(), // Links to Auth0 user for individuals
    auth0OrgId: z.string().optional(), // Links to Auth0 Organization for org accounts
    accountNumber: z.string(),
    name: z.string(),
    accountType: SetupAccountTypeSchema,
    // Whether this account is a law firm. Drives the pricing engine's client-type
    // resolution: law firms get baseline pricing, non-law-firm price rows carry a
    // surcharge. Defaults to true — law-firm is the baseline, so existing accounts
    // incur no non-law-firm surcharge.
    isLawFirm: z.boolean().default(true),
    // Customer category chosen at registration (SOR-129). Optional/nullable —
    // absent or null means unset. The DB-side provenance column
    // (customer_category_source) is deliberately NOT part of the API surface.
    customerCategory: CustomerCategorySchema.nullable().optional(),
    // Primary contact
    contactName: z.string().optional(),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().optional(),
    // Secondary contact
    secondaryContactName: z.string().optional(),
    secondaryContactEmail: z.string().email().optional(),
    secondaryContactPhone: z.string().optional(),
    // Address
    address: z.object({
        street1: z.string(),
        street2: z.string().optional(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
        country: z.string().default('US'),
    }).optional(),
    // Payment configuration
    paymentMethod: PaymentMethodSchema.default('invoice'),
    paymentFrequency: PaymentFrequencySchema.default('per-order'),
    paymentStatus: PaymentStatusSchema.default('current'),
    // Credit card / ACH settings
    preAuthEnabled: z.boolean().default(false),
    processingFeePercent: z.number().min(0).max(100).default(3.5),
    processingFeeMode: z.enum(['default', 'custom']).default('default'),
    processingFeeStatutory: z.boolean().default(true),
    processingFeeEfm: z.boolean().default(true),
    processingFeeService: z.boolean().default(true),
    // Retainer settings
    retainerEnabled: z.boolean().default(false),
    retainerBalance: z.number().default(0),
    retainerMinimum: z.number().default(0),
    // When true, the account is NOT charged for filings rejected by the court.
    // Defaults to false — self-service customers continue to pay for rejected
    // filings, matching the existing billing default. Admins toggle per-account.
    waiveRejectionFee: z.boolean().default(false),
    // QuickBooks integration
    quickbooksCustomerId: z.string().optional(),
    quickbooksSyncStatus: QuickBooksSyncStatusSchema.default('not-linked'),
    quickbooksLastSync: z.string().datetime().optional(),
    // Service office / logistics
    serviceOfficeId: z.string().uuid().optional(),
    route: z.string().optional(),
    pickupTime: z.string().optional(),
    // Billing options
    showPricingAtCheckout: z.boolean().default(true),
    payInvoicesInGroups: z.boolean().default(false),
    paymentProcessingEnabled: z.boolean().default(true),
    // eFile
    efileAccountNumber: z.string().optional(),
    // Notes
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    // Status
    status: SetupAccountStatusSchema.default('active'),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAccountSchema = AccountSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateAccountSchema = AccountSchema.partial().omit({
    id: true,
    createdAt: true,
});
// Account preview for hover displays
export const AccountPreviewSchema = z.object({
    id: z.string().uuid(),
    accountNumber: z.string(),
    name: z.string(),
    accountType: SetupAccountTypeSchema,
    contactName: z.string().optional(),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string().optional(),
    paymentMethod: PaymentMethodSchema,
    paymentStatus: PaymentStatusSchema,
    status: SetupAccountStatusSchema,
    totalOrders: z.number().optional(),
    lastOrderDate: z.string().datetime().optional(),
});
// Processing fee mode
export const ProcessingFeeModeSchema = z.enum(['default', 'custom']);
